import { useEffect, useRef, useState } from "react";
import { useLantern, useSession } from "../hooks";
import { navigate } from "../router";
import { looksUnread, type ChannelState } from "../sync/store";
import type { Message, UUID } from "../sync/types";
import { grouped } from "../timeline";
import { MessageItem } from "./MessageItem";
import { EmptyState, Page } from "./ui";
import { t, tn } from "../i18n";

// At most this many of a channel's new messages show here; the channel
// has the rest.
const shown = 20;

// UnreadsView gathers what is new in every channel one follows, newest
// channel first, to read in one place. A channel stays until marked read,
// and then folds to a line, so the page does not jump while one reads.
export function UnreadsView() {
  const state = useLantern();
  const session = useSession();
  // The channels listed: those unread when the page opened, then those
  // that become unread while it is open.
  const listed = useRef<UUID[]>([]);
  const unread = [...state.channels.values()].filter((cs) => !cs.channel.archived_at && looksUnread(cs.channel));
  for (const cs of unread.sort((a, b) => (b.channel.last_message_at ?? "").localeCompare(a.channel.last_message_at ?? ""))) {
    if (!listed.current.includes(cs.channel.id)) listed.current.push(cs.channel.id);
  }
  const channels = listed.current.map((id) => state.channels.get(id)).filter((cs): cs is ChannelState => !!cs);
  const left = channels.filter((cs) => looksUnread(cs.channel));

  return (
    <Page
      className="unreads-view"
      title={t("All unreads")}
      description={t("What is new in your channels and DMs, newest first. Mark a channel read when you are done with it.")}
      actions={
        left.length > 1 && (
          <button className="secondary" onClick={() => left.forEach((cs) => void session.engine.markRead(cs.channel.id))}>
            {t("Mark all as read")}
          </button>
        )
      }
    >
      {channels.length === 0 && (
        <EmptyState icon="🎉" title={t("You're all caught up")}>
          {t("New messages in your channels and DMs show up here.")}
        </EmptyState>
      )}
      {channels.map((cs) => (
        <UnreadChannel key={cs.channel.id} cs={cs} />
      ))}
    </Page>
  );
}

function UnreadChannel({ cs }: { cs: ChannelState }) {
  const session = useSession();
  const id = cs.channel.id;
  // Where one had read to when the page opened, so marking it read leaves
  // what was shown.
  const [since] = useState(cs.channel.membership?.last_read_seq ?? 0);
  const [messages, setMessages] = useState<Message[] | null>(null);
  const [error, setError] = useState("");
  const read = !looksUnread(cs.channel);
  useEffect(() => {
    let live = true;
    session.api.history(id, 0, 50).then(
      (r) => live && setMessages(r.messages.filter((m) => m.seq > since && !m.thread_root_id && m.class === "human").reverse()),
      (err) => live && setError(String(err)),
    );
    return () => {
      live = false;
    };
  }, [session, id, since, cs.lastSeq]);

  const name = session.channelName(id) || t("a conversation");
  const list = messages ?? [];
  const hidden = list.length - shown;
  if (read)
    return (
      <p className="card unread-done" aria-label={t("{name}: read", { name })}>
        <button className="link" onClick={() => navigate(`/c/${id}`)}>
          {name}
        </button>{" "}
        <span className="muted">{t("marked as read")}</span>
      </p>
    );
  return (
    <article className="card thread-card unread-channel" aria-label={t("Unread in {name}", { name })}>
      <header className="thread-card-header">
        <button className="link" onClick={() => navigate(`/c/${id}`)}>
          {name}
        </button>
        <span className="muted">
          {t("{n} new", { n: cs.channel.membership?.unread_count ?? 0 })}
          {(cs.channel.membership?.mention_count ?? 0) > 0 && ` · ${t("{n} for you", { n: cs.channel.membership!.mention_count })}`}
        </span>
        <button className="small" onClick={() => void session.engine.markRead(id)}>
          {t("Mark as read")}
        </button>
      </header>
      {error && <p className="error">{error}</p>}
      {messages && list.length === 0 && <p className="muted unread-replies">{t("New replies in threads. Open the channel to see them.")}</p>}
      {hidden > 0 && (
        <button className="more-replies" onClick={() => navigate(`/c/${id}`)}>
          {tn(hidden, "{n} earlier message", "{n} earlier messages")}
        </button>
      )}
      {list.slice(-shown).map((m, i, all) => (
        <MessageItem key={m.id} m={m} compact={grouped(all[i - 1], m)} />
      ))}
    </article>
  );
}
